import React from "react";
import NavBar from "./NavBar";
import Footer from "./Footer";

const PrivacyPolicy = () => {
  const [darkMode, setDarkMode] = React.useState(false);

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const Section = ({ title, children }) => (
    <div className="mb-12">
      <h2
        className={
          darkMode
            ? "text-2xl font-bold mb-4 text-gray-200"
            : "text-2xl font-bold mb-4 text-gray-800"
        }
      >
        {title}
      </h2>
      <div
        className={
          darkMode ? "space-y-4 text-gray-300" : "space-y-4 text-gray-600"
        }
      >
        {children}
      </div>
    </div>
  );

  const SubSection = ({ title, children }) => (
    <div className="mb-6">
      <h3
        className={
          darkMode
            ? "text-xl font-semibold mb-3 text-gray-300"
            : "text-xl font-semibold mb-3 text-gray-700"
        }
      >
        {title}
      </h3>
      <div
        className={
          darkMode ? "space-y-2 text-gray-300" : "space-y-2 text-gray-600"
        }
      >
        {children}
      </div>
    </div>
  );

  const List = ({ items }) => (
    <ul className="list-disc pl-6 space-y-2">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );

  const mainClassName = darkMode
    ? "flex-grow transition-colors duration-300 dark bg-gray-900 text-white"
    : "flex-grow transition-colors duration-300 bg-white text-gray-800";

  const titleClassName = darkMode
    ? "text-4xl font-bold mb-8 text-gray-100"
    : "text-4xl font-bold mb-8 text-gray-900";

  const lastUpdatedClassName = darkMode
    ? "text-lg mb-12 text-gray-300"
    : "text-lg mb-12 text-gray-600";

  return (
    <div className="flex flex-col min-h-screen">
      <main className={mainClassName}>
        <NavBar darkMode={darkMode} />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className={titleClassName}>Privacy Policy</h1>

          <p className={lastUpdatedClassName}>
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <Section title="1. Introduction">
            <p>
              At Boxia, we take your privacy seriously. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your
              information when you use our productivity platform. Please read
              this privacy policy carefully. If you do not agree with the terms
              of this privacy policy, please do not access the application.
            </p>
          </Section>

          <Section title="2. Data Collection">
            <SubSection title="What Data We Collect">
              <List
                items={[
                  "Personal Information: Name, email address, profile information, and account credentials.",
                  "Usage Data: How you interact with our platform, features used, and preferences.",
                  "Payment Information: When you subscribe to our paid plans (processed securely through our payment processors).",
                  "Integration Data: Information from third-party services you connect (Google, Microsoft, GitHub, etc.).",
                  "Technical Data: IP address, browser type, device information, cookies, and analytics data.",
                ]}
              />
            </SubSection>

            <SubSection title="How We Collect Data">
              <List
                items={[
                  "Direct Collection: Information you provide when creating an account, updating your profile, or using our services.",
                  "Automatic Collection: Through cookies, log files, and analytics tools when you use our platform.",
                  "Third-Party Services: When you connect external services like Google, Microsoft, or GitHub.",
                ]}
              />
            </SubSection>
          </Section>

          <Section title="3. How We Use Your Data">
            <List
              items={[
                "To provide and maintain our service, including processing your transactions and managing your account.",
                "To personalize your experience and improve our platform based on your preferences and usage patterns.",
                "To communicate with you about service updates, security alerts, and support messages.",
                "To prevent, detect, and address technical issues and potential security threats.",
                "To comply with legal obligations and enforce our terms of service.",
              ]}
            />
          </Section>

          <Section title="4. Data Sharing & Disclosure">
            <p>We may share your information with:</p>
            <List
              items={[
                "Service Providers: AWS (hosting), Supabase (database), and other essential service providers who assist in operating our platform.",
                "Third-Party Integrations: When you choose to connect services like Google, Microsoft, or GitHub.",
                "Legal Requirements: When required by law, regulation, or legal process.",
                "Business Transfers: In connection with any merger, sale of company assets, or acquisition.",
              ]}
            />
            <p className="mt-4 font-semibold">
              We do not sell your personal data to third parties.
            </p>
          </Section>

          <Section title="5. Data Storage & Security">
            <List
              items={[
                "We use industry-standard security measures to protect your data, including encryption at rest and in transit.",
                "Data is primarily stored on AWS and Supabase infrastructure with appropriate security controls.",
                "We implement row-level security (RLS) and other database security measures.",
                "We retain your data only as long as necessary to provide our services or comply with legal obligations.",
                "You can request data deletion at any time through your account settings.",
              ]}
            />
          </Section>

          <Section title="6. User Rights & Control">
            <p>You have the right to:</p>
            <List
              items={[
                "Access and download your personal data",
                "Correct or update your information",
                "Delete your account and associated data",
                "Opt-out of marketing communications",
                "Manage your privacy settings and integration permissions",
              ]}
            />
          </Section>

          <Section title="7. Cookies & Tracking">
            <p>We use cookies and similar tracking technologies to:</p>
            <List
              items={[
                "Remember your preferences and settings",
                "Understand how you use our platform",
                "Improve our services and user experience",
                "Provide security and fraud prevention",
              ]}
            />
            <p className="mt-4">
              You can control cookie preferences through your browser settings.
            </p>
          </Section>

          <Section title="8. Third-Party Services & Integrations">
            <p>
              Our platform integrates with various third-party services (Google,
              Microsoft, GitHub, etc.). When you connect these services:
            </p>
            <List
              items={[
                "You're subject to their respective privacy policies",
                "We only access data necessary for the integration to function",
                "You can revoke access to these integrations at any time",
                "We maintain strict data handling practices with all integrated services",
              ]}
            />
          </Section>

          <Section title="9. International Users & Compliance">
            <SubSection title="GDPR Compliance (EU Users)">
              <List
                items={[
                  "Right to access your personal data",
                  "Right to rectification of inaccurate data",
                  "Right to erasure ('right to be forgotten')",
                  "Right to data portability",
                  "Right to object to processing",
                ]}
              />
            </SubSection>

            <SubSection title="CCPA Compliance (California Users)">
              <List
                items={[
                  "Right to know what personal information is collected",
                  "Right to delete personal information",
                  "Right to opt-out of the sale of personal information",
                  "Right to non-discrimination for exercising CCPA rights",
                ]}
              />
            </SubSection>
          </Section>

          <Section title="10. Changes to the Policy">
            <p>
              We may update this privacy policy from time to time. We will
              notify you of any changes by:
            </p>
            <List
              items={[
                "Posting the new privacy policy on this page",
                "Updating the 'Last updated' date at the top of this policy",
                "Sending you an email notification for significant changes",
                "Displaying a prominent notice within our application",
              ]}
            />
          </Section>

          <Section title="11. Contact Information">
            <p>
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <div className="mt-4">
              <p>Email: privacy@boxia.io</p>
              <p>Address: 95-2200 Panorama Drive, Port Moody, BC, V3H 5M2</p>
            </div>
          </Section>
        </div>

        <Footer darkMode={darkMode} />
      </main>
    </div>
  );
};

export default PrivacyPolicy;
